/**
 * Who brought whom: partners' registrations to decide, the customers each
 * partner is credited with, and disputes.
 *
 * A registration that looks like a customer the organization already has, or
 * one another partner registered, is marked a possible duplicate and shows what
 * matched. Approving it anyway is an **existing-customer exception**: the
 * client is chosen and the reason is required, and the trail keeps both.
 * Reassigning a customer ends one attribution and starts another; what the
 * first partner earned stays with it.
 */

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Can,
  Card,
  CardBody,
  DetailField,
  DetailGrid,
  Drawer,
  LoadingState,
  PageHeader,
  RecordSelect,
  ServerTable,
  Tabs,
  Textarea,
  createServerFetcher,
  formatDate,
  formatDateTime,
  humanize,
  toast,
  type ServerColumn,
} from '@connect/web-shared';

import {
  REGISTRATION_STATUS,
  SERVICE_LABELS,
  partnerKeys,
  partnersApi,
  type AttributionRow,
  type RegistrationRow,
} from './api';
import { Chip, Failed, ReasonDialog, errorText } from './shared';

type Tab = 'registrations' | 'customers' | 'disputes';

export function PartnerRegistrationsPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab | null) ?? 'registrations';
  return (
    <div className="flex flex-1 flex-col gap-4">
      <PageHeader
        title="Partner customers"
        description="Registrations to decide, who each customer is credited to, and disputes."
        tabs={
          <Tabs
            value={tab}
            onChange={(id) => setParams({ tab: id }, { replace: true })}
            items={[
              { id: 'registrations', label: 'Registrations' },
              { id: 'customers', label: 'Credited customers' },
              { id: 'disputes', label: 'Disputes' },
            ]}
          />
        }
      />
      {tab === 'registrations' ? <Registrations /> : tab === 'customers' ? <Attributions /> : <Disputes />}
    </div>
  );
}

function Registrations() {
  const [open, setOpen] = useState<string | null>(null);
  const fetchRows = useMemo(() => createServerFetcher<RegistrationRow>('/partners/registrations'), []);
  const columns = useMemo<ServerColumn<RegistrationRow>[]>(
    () => [
      { field: 'reference', header: 'Reference', filter: 'text', width: 140 },
      { field: 'company_name', header: 'Company', filter: 'text', minWidth: 200 },
      { field: 'partner', header: 'Partner', filter: 'text', minWidth: 170 },
      {
        field: 'status',
        header: 'Status',
        filter: 'select',
        options: Object.entries(REGISTRATION_STATUS).map(([value, v]) => ({ value, label: v.label })),
        width: 160,
        render: (row) => <Chip value={row.status} map={REGISTRATION_STATUS} />,
      },
      {
        field: 'match_reasons',
        header: 'Matched on',
        sortable: false,
        width: 160,
        render: (row) => row.match_reasons.map(humanize).join(', ') || '—',
      },
      { field: 'submitted_at', header: 'Registered', filter: 'date', width: 130, format: (v) => formatDate(v as string) },
    ],
    [],
  );
  return (
    <>
      <ServerTable<RegistrationRow>
        columns={columns}
        fetchRows={fetchRows}
        rowKey={(row) => row.public_id}
        queryKey={partnerKeys.registrations}
        stateKey="partners.registrations"
        searchPlaceholder="Search by company, partner or reference"
        onView={(row) => setOpen(row.public_id)}
        emptyTitle="No registrations"
        emptyDescription="Customers partners register appear here for a decision."
      />
      <RegistrationDrawer id={open} onClose={() => setOpen(null)} />
    </>
  );
}

function RegistrationDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: partnerKeys.registration(id ?? ''),
    queryFn: () => partnersApi.registration(id!),
    enabled: id !== null,
  });
  const [rejecting, setRejecting] = useState(false);
  const [exception, setException] = useState<string | null>(null);
  const [why, setWhy] = useState('');
  const decide = useMutation({
    mutationFn: (body: Record<string, unknown>) => partnersApi.decide(id!, body),
    onSuccess: async () => {
      toast.success('Decided');
      setRejecting(false);
      await queryClient.invalidateQueries({ queryKey: partnerKeys.all });
    },
  });
  const data = detail.data;
  const open = data && ['submitted', 'duplicate_review'].includes(data.registration.status);
  return (
    <Drawer open={id !== null} onClose={onClose} title={data?.registration.company_name ?? 'Registration'} width="lg">
      {detail.isPending ? (
        <LoadingState />
      ) : detail.isError ? (
        <Failed error={detail.error} />
      ) : data ? (
        <div className="space-y-4">
          <DetailGrid>
            <DetailField label="Partner">{data.registration.partner}</DetailField>
            <DetailField label="Status">
              <Chip value={data.registration.status} map={REGISTRATION_STATUS} />
            </DetailField>
            <DetailField label="Contact">{data.registration.contact_name}</DetailField>
            <DetailField label="Email">{data.registration.email}</DetailField>
            <DetailField label="Phone">{data.registration.phone}</DetailField>
            <DetailField label="Tax id">{data.registration.tax_id}</DetailField>
            <DetailField label="City">{data.registration.city}</DetailField>
            <DetailField label="Interested in">
              {data.registration.services.map((s) => SERVICE_LABELS[s]).join(', ') || '—'}
            </DetailField>
          </DetailGrid>
          {data.registration.notes ? <p className="rounded-md bg-surface-2 p-3 text-sm">{data.registration.notes}</p> : null}
          {data.matched_client ? (
            <Alert tone="warning" title={`Looks like ${data.matched_client.name}`}>
              Matched on {data.registration.match_reasons.map(humanize).join(', ')}.{' '}
              {data.matched_client.credited_to
                ? `Already credited to ${data.matched_client.credited_to} — reassign it instead if it should move.`
                : 'Not credited to any partner.'}
            </Alert>
          ) : data.registration.match_reasons.includes('another_partner') ? (
            <Alert tone="warning">Another partner registered this customer first.</Alert>
          ) : null}
          {open ? (
            <Can permission="partners.attribution.approve">
              <div className="space-y-3 rounded-md border border-line p-3">
                {decide.isError ? <Alert tone="danger">{errorText(decide.error)}</Alert> : null}
                {data.matched_client ? (
                  <>
                    <p className="text-sm">
                      To credit the partner with this existing customer, say why. This is an exception and is kept on record.
                    </p>
                    <Textarea label="Reason" value={why} onChange={(e) => setWhy(e.target.value)} rows={2} />
                    <Button
                      disabled={why.trim().length < 3}
                      loading={decide.isPending}
                      onClick={() =>
                        decide.mutate({
                          decision: 'approve',
                          existing_client: data.matched_client!.public_id,
                          reason: why.trim(),
                        })
                      }
                    >
                      Approve as an exception
                    </Button>
                  </>
                ) : (
                  <div className="flex flex-wrap items-end gap-2">
                    <Button loading={decide.isPending} onClick={() => decide.mutate({ decision: 'approve' })}>
                      Approve — create the client
                    </Button>
                    <div className="min-w-60 flex-1 space-y-1">
                      <p className="text-sm font-medium">…or credit an existing client (an exception)</p>
                      <RecordSelect
                        endpoint="/crm/clients"
                        value={exception}
                        onChange={(value) => setException(value)}
                        toOption={(row) => ({ value: String(row.public_id), label: String(row.name) })}
                        placeholder="Choose a client"
                        clearable
                      />
                    </div>
                    {exception ? (
                      <>
                        <Textarea label="Reason" value={why} onChange={(e) => setWhy(e.target.value)} rows={2} />
                        <Button
                          variant="secondary"
                          disabled={why.trim().length < 3}
                          onClick={() => decide.mutate({ decision: 'approve', existing_client: exception, reason: why.trim() })}
                        >
                          Approve as an exception
                        </Button>
                      </>
                    ) : null}
                  </div>
                )}
                <Button variant="danger" onClick={() => setRejecting(true)}>
                  Reject
                </Button>
              </div>
            </Can>
          ) : null}
          <div>
            <h3 className="mb-2 text-sm font-medium">History</h3>
            <ol className="space-y-1 text-sm">
              {data.events.map((e) => (
                <li key={`${e.at}-${e.kind}`} className="text-muted">
                  {formatDateTime(e.at)} · {humanize(e.kind)} by {e.by}
                  {typeof e.detail.reason === 'string' ? ` — ${e.detail.reason}` : ''}
                </li>
              ))}
            </ol>
          </div>
        </div>
      ) : null}
      <ReasonDialog
        open={rejecting}
        title="Reject this registration"
        description="The partner sees this reason."
        confirmLabel="Reject"
        variant="danger"
        busy={decide.isPending}
        error={decide.error}
        onClose={() => setRejecting(false)}
        onConfirm={(reason) => decide.mutate({ decision: 'reject', reason })}
      />
    </Drawer>
  );
}

function Attributions() {
  const queryClient = useQueryClient();
  const [moving, setMoving] = useState<AttributionRow | null>(null);
  const [ending, setEnding] = useState<AttributionRow | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const fetchRows = useMemo(() => createServerFetcher<AttributionRow>('/partners/attributions'), []);
  const done = async () => {
    setMoving(null);
    setEnding(null);
    setTo(null);
    toast.success('Saved');
    await queryClient.invalidateQueries({ queryKey: partnerKeys.all });
  };
  const reassign = useMutation({
    mutationFn: (reason: string) => partnersApi.reassign(moving!.public_id, to!, reason),
    onSuccess: done,
  });
  const end = useMutation({
    mutationFn: (reason: string) => partnersApi.endAttribution(ending!.public_id, reason),
    onSuccess: done,
  });
  const columns = useMemo<ServerColumn<AttributionRow>[]>(
    () => [
      { field: 'client', header: 'Customer', filter: 'text', minWidth: 190 },
      { field: 'partner', header: 'Partner', filter: 'text', minWidth: 170 },
      {
        field: 'status',
        header: 'Status',
        filter: 'select',
        options: [
          { value: 'active', label: 'Active' },
          { value: 'ended', label: 'Ended' },
        ],
        width: 110,
      },
      { field: 'source', header: 'How', width: 160, format: (v) => humanize(v as string) },
      { field: 'since', header: 'Since', filter: 'date', width: 130, format: (v) => formatDate(v as string) },
      { field: 'until', header: 'Until', sortable: false, width: 130, format: (v) => formatDate(v as string | null) },
    ],
    [],
  );
  return (
    <>
      <ServerTable<AttributionRow>
        columns={columns}
        fetchRows={fetchRows}
        rowKey={(row) => row.public_id}
        queryKey={partnerKeys.attributions}
        stateKey="partners.attributions"
        searchPlaceholder="Search by customer or partner"
        rowActions={(row) =>
          row.status === 'active'
            ? [
                { label: 'Reassign to another partner', onSelect: () => setMoving(row) },
                { label: 'End', destructive: true, onSelect: () => setEnding(row) },
              ]
            : []
        }
        rowDetailFields={(row) => [{ label: 'Ended because', value: row.ended_reason }]}
        emptyTitle="No customers credited yet"
      />
      <ReasonDialog
        open={moving !== null}
        title={`Reassign ${moving?.client ?? ''}`}
        description="From now the customer's spending earns the new partner. What the current partner earned stays with it."
        confirmLabel="Reassign"
        busy={reassign.isPending}
        error={reassign.error}
        onClose={() => setMoving(null)}
        onConfirm={(reason) => to && reassign.mutate(reason)}
      >
        <div className="space-y-1">
          <p className="text-sm font-medium">New partner</p>
          <RecordSelect
            endpoint="/partners"
            params={{ filters: JSON.stringify({ status: { type: 'select', values: ['active'] } }) }}
            value={to}
            onChange={(value) => setTo(value)}
            toOption={(row) => ({ value: String(row.public_id), label: String(row.name) })}
            placeholder="Choose an active partner"
          />
        </div>
      </ReasonDialog>
      <ReasonDialog
        open={ending !== null}
        title={`Stop crediting ${ending?.client ?? ''}`}
        description="From now the customer earns no partner commission."
        confirmLabel="End"
        variant="danger"
        busy={end.isPending}
        error={end.error}
        onClose={() => setEnding(null)}
        onConfirm={(reason) => end.mutate(reason)}
      />
    </>
  );
}

function Disputes() {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: partnerKeys.disputes, queryFn: () => partnersApi.disputes() });
  const [resolving, setResolving] = useState<{ id: string; decision: 'upheld' | 'dismissed' } | null>(null);
  const resolve = useMutation({
    mutationFn: (resolution: string) => partnersApi.resolveDispute(resolving!.id, { decision: resolving!.decision, resolution }),
    onSuccess: async () => {
      setResolving(null);
      await queryClient.invalidateQueries({ queryKey: partnerKeys.disputes });
    },
  });
  if (list.isPending) return <LoadingState />;
  if (list.isError) return <Failed error={list.error} onRetry={() => void list.refetch()} />;
  return (
    <Card>
      <CardBody className="divide-y divide-line">
        {list.data.length === 0 ? <p className="text-sm text-muted">No disputes.</p> : null}
        {list.data.map((d) => (
          <div key={d.public_id} className="flex flex-wrap items-start justify-between gap-3 py-3 text-sm">
            <div className="max-w-2xl space-y-1">
              <p className="font-medium">{d.partner}</p>
              <p>{d.reason}</p>
              {d.resolution ? <p className="text-muted">Resolution: {d.resolution}</p> : null}
              <p className="text-xs text-faint">{formatDateTime(d.at)}</p>
            </div>
            {d.status === 'open' ? (
              <Can permission="partners.attribution.approve">
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => setResolving({ id: d.public_id, decision: 'upheld' })}>
                    Uphold
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setResolving({ id: d.public_id, decision: 'dismissed' })}>
                    Dismiss
                  </Button>
                </div>
              </Can>
            ) : (
              <span className="text-muted">{humanize(d.status)}</span>
            )}
          </div>
        ))}
      </CardBody>
      <ReasonDialog
        open={resolving !== null}
        title={resolving?.decision === 'upheld' ? 'Uphold the dispute' : 'Dismiss the dispute'}
        description="The partner sees this answer. Upholding does not move the customer by itself — reassign it under Credited customers."
        confirmLabel={resolving?.decision === 'upheld' ? 'Uphold' : 'Dismiss'}
        busy={resolve.isPending}
        error={resolve.error}
        onClose={() => setResolving(null)}
        onConfirm={(resolution) => resolve.mutate(resolution)}
      />
    </Card>
  );
}
